<template>
    <div class="stress-test-page">
        <div class="st-header">
            <h1>⚡ Stress Test</h1>
            <span class="st-badge" :class="statusClass">{{ statusText }}</span>
        </div>

        <!-- Config -->
        <div class="st-card" v-if="!running && !results">
            <h2>Настройки</h2>

            <div class="st-form">
                <div class="st-field">
                    <label>Тип теста</label>
                    <select v-model="config.testType">
                        <option value="baseline">Baseline (100 VU, контрольный)</option>
                        <option value="spike">Spike (мгновенный скачок)</option>
                        <option value="stress">Stress (плавный рост)</option>
                    </select>
                </div>

                <div class="st-field">
                    <label>Количество VU</label>
                    <input
                        type="number"
                        v-model.number="config.vus"
                        min="1"
                        max="50000"
                        :disabled="config.testType === 'baseline'" />
                    <span class="st-hint" v-if="config.testType === 'baseline'">Baseline фиксирует 100 VU</span>
                </div>

                <div class="st-field">
                    <label>Target URL</label>
                    <input type="text" v-model="config.targetUrl" placeholder="http://localhost:3000" />
                </div>
            </div>

            <button class="st-btn st-btn-primary" @click="startTest" :disabled="starting">
                {{ starting ? "Запуск..." : "🚀 Запустить тест" }}
            </button>

            <div class="st-warning" v-if="config.vus >= 3000">
                ⚠️ {{ config.vus }} VU — серьёзная нагрузка. Убедись, что тестируешь не на проде.
            </div>
        </div>

        <!-- Progress -->
        <div class="st-card" v-if="running">
            <h2>Тест выполняется</h2>

            <div class="st-progress-bar">
                <div class="st-progress-fill" :style="{ width: progress + '%' }"></div>
            </div>
            <p class="st-progress-text">{{ progress }}%</p>

            <div class="st-meta">
                <span>Тип: {{ config.testType }}</span>
                <span>VU: {{ config.vus }}</span>
                <span>Начало: {{ startedAt }}</span>
            </div>

            <button class="st-btn st-btn-danger" @click="abortTest">⏹ Остановить</button>
        </div>

        <!-- Results -->
        <div class="st-card" v-if="results">
            <div class="st-results-header">
                <h2>📊 Результаты</h2>
                <span class="st-status-badge" :class="resultsStatusClass">{{ resultsStatusText }}</span>
            </div>

            <div class="st-metrics">
                <div class="st-metric">
                    <span class="st-metric-value">{{ r.totalRequests }}</span>
                    <span class="st-metric-label">Всего запросов</span>
                </div>
                <div class="st-metric">
                    <span class="st-metric-value">{{ r.requestsPerSecond.toFixed(1) }}</span>
                    <span class="st-metric-label">Req/sec</span>
                </div>
                <div class="st-metric">
                    <span class="st-metric-value">{{ (r.errorRate * 100).toFixed(2) }}%</span>
                    <span class="st-metric-label">Error Rate</span>
                </div>
            </div>

            <h3>Latency (ms)</h3>
            <table class="st-table">
                <thead>
                    <tr>
                        <th>p50</th>
                        <th>p95</th>
                        <th>p99</th>
                        <th>Avg</th>
                        <th>Max</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td :class="latencyClass(r.latency.p50)">
                            {{ Math.round(r.latency.p50) }}
                        </td>
                        <td :class="latencyClass(r.latency.p95)">
                            {{ Math.round(r.latency.p95) }}
                        </td>
                        <td :class="latencyClass(r.latency.p99)">
                            {{ Math.round(r.latency.p99) }}
                        </td>
                        <td>{{ Math.round(r.latency.avg) }}</td>
                        <td>{{ Math.round(r.latency.max) }}</td>
                    </tr>
                </tbody>
            </table>

            <h3>Пороги (Thresholds)</h3>
            <div class="st-thresholds" v-if="results.thresholds">
                <div class="st-threshold" v-for="(t, i) in results.thresholds" :key="i">
                    <span :class="t.passed ? 'st-pass' : 'st-fail'">{{ t.passed ? "✅" : "❌" }}</span>
                    <span>{{ t.metric }}: {{ t.thresholds.join(", ") }}</span>
                </div>
            </div>

            <h3>Рекомендации</h3>
            <div class="st-recommendations">
                <div v-for="(rec, i) in recommendations" :key="i" class="st-rec">{{ rec }}</div>
            </div>

            <div v-if="results.textReport" class="st-text-report">
                <h3>Текстовый отчёт k6</h3>
                <pre>{{ results.textReport }}</pre>
            </div>

            <div class="st-actions">
                <button class="st-btn st-btn-secondary" @click="resetTest">🔄 Новый тест</button>
            </div>
        </div>

        <!-- Error -->
        <div class="st-card st-error" v-if="error">
            <h2>❌ Ошибка</h2>
            <p>{{ error }}</p>
            <button class="st-btn st-btn-secondary" @click="resetTest">Попробовать снова</button>
        </div>
    </div>
</template>

<script>
export default {
    layout: "empty",

    data() {
        return {
            config: {
                testType: "spike",
                vus: 5000,
                targetUrl: `http://localhost:${process.env.PORT || 3000}`,
            },
            running: false,
            starting: false,
            progress: 0,
            startedAt: null,
            results: null,
            error: null,
            pollInterval: null,
            testId: null,
        };
    },

    computed: {
        r() {
            // Safe access to results with defaults
            const r = this.results || {};
            
            // Support both formats: old (r.results) and new (direct properties)
            const resultsData = r.results || r;
            const latencyData = resultsData.latency || {};
            
            // Extract metrics from k6 summary format
            const totalRequests = resultsData.totalRequests || 
                                 (resultsData.metrics && resultsData.metrics.http_reqs && resultsData.metrics.http_reqs.values.count) || 0;
            
            const errorRate = resultsData.errorRate || 
                             (resultsData.metrics && resultsData.metrics.http_req_failed && resultsData.metrics.http_req_failed.values.rate) || 0;
            
            const rps = resultsData.requestsPerSecond || 
                       (resultsData.metrics && resultsData.metrics.http_reqs && resultsData.metrics.http_reqs.values.rate) || 0;
            
            // Final fallback: parse from textReport (it's on r.textReport, not resultsData.textReport)
            let fallbackData = {};
            if (r.textReport && (totalRequests === 0 || rps === 0)) {
                fallbackData = this.parseMetricsFromTextReport(r.textReport);
            }
            
            return {
                totalRequests: totalRequests || fallbackData.totalRequests || 0,
                requestsPerSecond: rps || fallbackData.rps || 0,
                errorRate: errorRate || fallbackData.errorRate || 0,
                latency: {
                    p50: latencyData.p50 || 
                         (resultsData.metrics && resultsData.metrics.http_req_duration && resultsData.metrics.http_req_duration.values.med) || 
                         fallbackData.p50 || 0,
                    p95: latencyData.p95 || 
                         (resultsData.metrics && resultsData.metrics.http_req_duration && resultsData.metrics.http_req_duration.values.p95) || 
                         fallbackData.p95 || 0,
                    p99: latencyData.p99 || 
                         (resultsData.metrics && resultsData.metrics.http_req_duration && resultsData.metrics.http_req_duration.values.p99) || 
                         fallbackData.p99 || 0,
                    avg: latencyData.avg || 
                         (resultsData.metrics && resultsData.metrics.http_req_duration && resultsData.metrics.http_req_duration.values.avg) || 
                         fallbackData.avg || 0,
                    max: latencyData.max || 
                         (resultsData.metrics && resultsData.metrics.http_req_duration && resultsData.metrics.http_req_duration.values.max) || 
                         fallbackData.max || 0,
                },
            };
        },
        statusClass() {
            if (this.running) return "st-badge-running";
            if (this.results) return "st-badge-done";
            return "st-badge-idle";
        },
        statusText() {
            if (this.running) return "Выполняется";
            if (this.results) return "Завершён";
            return "Ожидание";
        },
        resultsStatusClass() {
            if (!this.results) return "";
            const p95 = this.r.latency.p95;
            const errRate = this.r.errorRate;
            if (errRate > 0.05 || p95 > 5000) return "st-status-critical";
            if (p95 > 2000) return "st-status-warning";
            return "st-status-ok";
        },
        resultsStatusText() {
            if (!this.results) return "";
            const p95 = this.r.latency.p95;
            const errRate = this.r.errorRate;
            if (errRate > 0.05 || p95 > 5000) return "🔴 КРИТИЧНО";
            if (p95 > 2000) return "🟡 ДОПУСТИМО";
            return "🟢 ОТЛИЧНО";
        },
        recommendations() {
            if (!this.results) return [];
            const r = this.r;
            const recs = [];

            if (r.errorRate > 0.05) {
                recs.push(
                    "🔴 Error rate > 5% — сервер не справляется. Проверь логи, увеличь ресурсы или добавь кэширование."
                );
            }
            if (r.latency.p95 > 5000) {
                recs.push(
                    "🔴 p95 > 5s — критически медленный SSR. Проверь: 1) Тяжёлые asyncData/fetch 2) Внешние API запросы 3) Утечки памяти."
                );
            }
            if (r.latency.p95 > 2000 && r.latency.p95 <= 5000) {
                recs.push(
                    "🟡 p95 > 2s — медленный отклик. Рассмотри: 1) Кэширование SSR (renderCached) 2) Оптимизацию asyncData 3) CDN для статики."
                );
            }
            if (r.latency.p99 > r.latency.p95 * 3) {
                recs.push(
                    "⚠️ p99 >> p95 — есть выбросы. Возможны: долгие GC паузы, медленные API ответы, блокировки event loop."
                );
            }
            if (r.latency.p95 <= 500) {
                recs.push("🟢 p95 < 500ms — отличный результат. Сервер хорошо справляется с нагрузкой.");
            }
            if (this.config.vus >= 3000 && r.requestsPerSecond < this.config.vus * 0.5) {
                recs.push(
                    "💡 RPS ниже ожидаемого при " +
                        this.config.vus +
                        " VU — возможно, bottleneck на стороне генератора нагрузки. Запусти k6 с распределённого генератора."
                );
            }
            if (recs.length === 0) {
                recs.push("ℹ️ Результаты в норме. Для глубокой диагностики запусти clinic.js.");
            }

            return recs;
        },
    },
    watch: {
        "config.testType": function (newType) {
            if (newType === "baseline") {
                this.config.vus = 100;
            }
        },
    },
    methods: {
        parseMetricsFromTextReport(textReport) {
            try {
                const metrics = {};
                
                // Parse: "Total Reqs: 123"
                const totalReqsMatch = textReport.match(/Total Reqs:\s*(\d+)/);
                if (totalReqsMatch) metrics.totalRequests = parseInt(totalReqsMatch[1]);
                
                // Parse: "RPS: 12.3"  
                const rpsMatch = textReport.match(/RPS:\s*([\d.]+)/);
                if (rpsMatch) metrics.rps = parseFloat(rpsMatch[1]);
                
                // Parse: "Error Rate: 1.23%"
                const errorRateMatch = textReport.match(/Error Rate:\s*([\d.]+)%/);
                if (errorRateMatch) metrics.errorRate = parseFloat(errorRateMatch[1]) / 100;
                
                // Parse latency metrics
                const avgMatch = textReport.match(/avg:\s*(\d+)/);
                const p95Match = textReport.match(/p95:\s*(\d+)/);
                const p99Match = textReport.match(/p99:\s*(\d+)/);
                const maxMatch = textReport.match(/max:\s*(\d+)/);
                
                if (avgMatch) metrics.avg = parseInt(avgMatch[1]);
                if (p95Match) metrics.p95 = parseInt(p95Match[1]);
                if (p99Match) metrics.p99 = parseInt(p99Match[1]);
                if (maxMatch) metrics.max = parseInt(maxMatch[1]);
                
                return metrics;
            } catch (e) {
                console.error('Error parsing text report:', e);
                return {};
            }
        },
        
        async startTest() {
            this.starting = true;
            this.error = null;
            this.results = null;

            try {
                const res = await this.$axios.post("/local-api/run-stress-test/", {
                    testType: this.config.testType,
                    vus: this.config.testType === "baseline" ? 100 : this.config.vus,
                    targetUrl: this.config.targetUrl || undefined,
                });

                this.testId = res.data.testId;
                this.running = true;
                this.startedAt = new Date().toLocaleTimeString();
                this.startPolling();
            } catch (e) {
                this.error = e.response?.data?.error || e.message || "Не удалось запустить тест";
            } finally {
                this.starting = false;
            }
        },

        startPolling() {
            this.pollInterval = setInterval(async () => {
                try {
                    const res = await this.$axios.get("/local-api/stress-test-status/");
                    const data = res.data;

                    if (data.status === "running") {
                        this.progress = data.progress || this.progress;
                    }

                    if (data.finished || data.status === "failed" || data.status === "aborted") {
                        this.running = false;
                        this.progress = data.status === "failed" ? 0 : 100;
                        this.results = data.results || { results: {} };
                        if (data.status === "failed") {
                            this.error = "Тест завершился с ошибкой";
                        }
                        this.stopPolling();
                    }
                } catch (e) {
                    // Continue polling on error
                }
            }, 2000);
        },

        stopPolling() {
            if (this.pollInterval) {
                clearInterval(this.pollInterval);
                this.pollInterval = null;
            }
        },
        async abortTest() {
            try {
                await this.$axios.post("/local-api/stress-test-abort/");
            } catch (e) {
                // ignore
            }
            this.running = false;
            this.stopPolling();
        },

        resetTest() {
            this.running = false;
            this.results = null;
            this.error = null;
            this.progress = 0;
            this.testId = null;
        },

        latencyClass(val) {
            if (val <= 500) return "st-latency-ok";
            if (val <= 2000) return "st-latency-warn";
            return "st-latency-bad";
        },
    },

    beforeDestroy() {
        this.stopPolling();
    },

    head() {
        return {
            title: "Stress Test — PPS",
            meta: [{ hid: "robots", name: "robots", content: "noindex, nofollow" }],
        };
    },
};
</script>

<style scoped>
.stress-test-page {
    max-width: 800px;
    margin: 40px auto;
    padding: 0 20px;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.st-header {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 24px;
}

.st-header h1 {
    margin: 0;
    font-size: 28px;
}

.st-badge {
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 13px;
    font-weight: 600;
}

.st-badge-idle {
    background: #f1f5f9;
    color: #64748b;
}
.st-badge-running {
    background: #fef3c7;
    color: #d97706;
}
.st-badge-done {
    background: #d1fae5;
    color: #059669;
}

.st-card {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    padding: 24px;
    margin-bottom: 16px;
}

.st-card h2 {
    margin: 0 0 16px;
    font-size: 20px;
}

.st-card h3 {
    margin: 20px 0 8px;
    font-size: 16px;
    color: #475569;
}

.st-form {
    display: grid;
    gap: 16px;
    margin-bottom: 20px;
}

.st-field label {
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    margin-bottom: 4px;
}

.st-field select,
.st-field input[type="text"],
.st-field input[type="number"] {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 14px;
    box-sizing: border-box;
}

.st-hint {
    font-size: 12px;
    color: #9ca3af;
}

.st-checkbox {
    display: flex;
    align-items: center;
    gap: 8px;
}

.st-checkbox label {
    margin: 0 !important;
    font-weight: 400 !important;
}

.st-btn {
    padding: 10px 24px;
    border: none;
    border-radius: 10px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.st-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.st-btn-primary {
    background: #4f46e5;
    color: #fff;
}

.st-btn-primary:hover:not(:disabled) {
    background: #4338ca;
}

.st-btn-secondary {
    background: #f1f5f9;
    color: #475569;
}

.st-btn-danger {
    background: #fee2e2;
    color: #dc2626;
}

.st-warning {
    margin-top: 12px;
    padding: 10px 16px;
    background: #fef3c7;
    border-radius: 8px;
    font-size: 13px;
    color: #92400e;
}

/* Progress */
.st-progress-bar {
    height: 8px;
    background: #e2e8f0;
    border-radius: 4px;
    overflow: hidden;
    margin-bottom: 8px;
}

.st-progress-fill {
    height: 100%;
    background: linear-gradient(90deg, #4f46e5, #7c3aed);
    transition: width 0.5s ease;
    border-radius: 4px;
}

.st-progress-text {
    text-align: center;
    font-size: 24px;
    font-weight: 700;
    color: #4f46e5;
    margin: 8px 0;
}

.st-meta {
    display: flex;
    gap: 16px;
    font-size: 13px;
    color: #64748b;
    margin-bottom: 16px;
}

/* Results */
.st-results-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
}

.st-results-header h2 {
    margin: 0 !important;
}

.st-status-badge {
    padding: 6px 14px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 700;
}

.st-status-ok {
    background: #d1fae5;
    color: #059669;
}
.st-status-warning {
    background: #fef3c7;
    color: #d97706;
}
.st-status-critical {
    background: #fee2e2;
    color: #dc2626;
}

.st-metrics {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    margin-bottom: 20px;
}

.st-metric {
    text-align: center;
    padding: 16px;
    background: #f8fafc;
    border-radius: 12px;
}

.st-metric-value {
    display: block;
    font-size: 28px;
    font-weight: 700;
    color: #1e293b;
}

.st-metric-label {
    font-size: 13px;
    color: #64748b;
}

/* Table */
.st-table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 16px;
}

.st-table th,
.st-table td {
    padding: 10px 16px;
    text-align: center;
    border-bottom: 1px solid #e2e8f0;
}

.st-table th {
    font-size: 13px;
    color: #64748b;
    font-weight: 600;
}

.st-table td {
    font-size: 16px;
    font-weight: 600;
}

.st-latency-ok {
    color: #059669;
}
.st-latency-warn {
    color: #d97706;
}
.st-latency-bad {
    color: #dc2626;
}

/* Thresholds */
.st-threshold {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 0;
    font-size: 14px;
}

.st-pass {
    color: #059669;
}
.st-fail {
    color: #dc2626;
}

/* Recommendations */
.st-recommendations {
    display: grid;
    gap: 8px;
}

.st-rec {
    padding: 10px 16px;
    background: #f8fafc;
    border-radius: 8px;
    font-size: 14px;
    line-height: 1.5;
}

.st-actions {
    margin-top: 20px;
}

.st-error {
    border-color: #fecaca;
}

.st-error h2 {
    color: #dc2626;
}

.st-text-report pre {
    background: #1e293b;
    color: #e2e8f0;
    padding: 16px;
    border-radius: 12px;
    overflow-x: auto;
    font-size: 13px;
    line-height: 1.4;
    white-space: pre;
    margin: 0;
}
</style>
